import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import he from 'he';
import './Leaderboard.css';
import api from '../utils/api';

const Leaderboard = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [topWinners, setTopWinners] = useState([]);
  const [topMessages, setTopMessages] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadLeaderboard();
  }, []);

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/leaderboard');
      setTopWinners(response.data.topWinners || []);
      setTopMessages(response.data.topMessages || []);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRankEmoji = (rank) => {
    if (rank === 1) return '🥇';
    if (rank === 2) return '🥈';
    if (rank === 3) return '🥉';
    return `#${rank}`;
  };

  if (loading) {
    return (
      <div className="leaderboard-container">
        <div className="leaderboard-window win98-bevel">
          <div className="loading-text">Loading leaderboard... Much Wow! <img src="/doge.png" alt="Doge" className="doge-inline-image" /></div>
        </div>
      </div>
    );
  }

  return (
    <div className="leaderboard-container">
      <div className="leaderboard-window win98-bevel">
        <div className="navigation-bar win98-bevel-inset">
          <div className="nav-links">
            <Link to="/" className={`nav-link ${location.pathname === '/' ? 'active' : ''}`}>
              💬 Chat
            </Link>
            <Link to="/puddle" className={`nav-link ${location.pathname === '/puddle' ? 'active' : ''}`}>
              💎 Puddle
            </Link>
            <Link to="/leaderboard" className={`nav-link ${location.pathname === '/leaderboard' ? 'active' : ''}`}>
              🏆 Leaderboard
            </Link>
            <Link to="/settings" className={`nav-link ${location.pathname === '/settings' ? 'active' : ''}`}>
              ⚙️ Settings
            </Link>
            <Link to="/faq" className={`nav-link ${location.pathname === '/faq' ? 'active' : ''}`}>
              ❓ FAQ
            </Link>
            <Link to="/tos" className={`nav-link ${location.pathname === '/tos' ? 'active' : ''}`}>
              📜 Rules/ToS
            </Link>
            <a 
              href="https://discord.gg/B26JbHZsqq" 
              target="_blank" 
              rel="noopener noreferrer" 
              className="nav-link discord-link"
              title="Join our Discord"
              style={{ display: 'flex', alignItems: 'center', gap: '6px' }}
            >
              <svg width="16" height="16" viewBox="0 0 71 55" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M60.1045 4.8978C55.5792 2.8214 50.7265 1.2916 45.6527 0.41542C45.5603 0.39851 45.468 0.440769 45.4204 0.525289C44.7963 1.6353 44.105 3.0824 43.6209 4.2216C38.1637 3.4046 32.7345 3.4046 27.3892 4.2216C26.905 3.0824 26.1886 1.6353 25.5617 0.525289C25.5141 0.443589 25.4218 0.40133 25.3294 0.41542C20.2584 1.2888 15.4057 2.8186 10.8776 4.8978C10.8384 4.9147 10.8048 4.9429 10.7825 4.9795C1.57795 18.7309 -0.943561 32.1443 0.293408 45.3914C0.299005 45.4562 0.335386 45.5182 0.385761 45.5576C6.45866 50.0174 12.3413 52.7249 18.1147 54.5195C18.2071 54.5477 18.305 54.5139 18.3638 54.4378C19.7295 52.5728 20.9469 50.6063 21.9907 48.5383C22.0523 48.4172 21.9935 48.2735 21.8676 48.2256C19.9366 47.4931 18.0979 46.6 16.3292 45.5858C16.1893 45.5041 16.1781 45.304 16.3068 45.2082C16.679 44.9293 17.0513 44.6391 17.4067 44.3461C17.471 44.2926 17.5606 44.2813 17.6362 44.3151C29.2558 49.6202 41.8354 49.6202 53.3179 44.3151C53.3935 44.2785 53.4831 44.2898 53.5502 44.3433C53.9057 44.6363 54.2779 44.9293 54.6529 45.2082C54.7816 45.304 54.7732 45.5041 54.6333 45.5858C52.8646 46.6197 51.0259 47.4931 49.0921 48.2228C48.9662 48.2707 48.9102 48.4172 49.9718 48.5383C51.038 50.6034 52.2554 52.5699 53.5951 54.435C53.651 54.5139 53.7489 54.5477 53.8413 54.5195C59.646 52.7249 65.5286 50.0174 71.6015 45.5576C71.6551 45.5182 71.6887 45.459 71.6943 45.3942C73.1747 30.0791 68.2147 16.7757 60.1968 4.9823C60.1772 4.9429 60.1437 4.9147 60.1045 4.8978ZM23.7259 37.3253C20.2276 37.3253 17.2991 34.8216 17.2991 31.6709C17.2991 28.5203 20.1717 26.0166 23.7259 26.0166C27.308 26.0166 30.1626 28.4935 30.1526 31.6709C30.1426 34.8483 27.308 37.3253 23.7259 37.3253ZM47.3178 37.3253C43.8196 37.3253 40.8911 34.8216 40.8911 31.6709C40.8911 28.5203 43.7636 26.0166 47.3178 26.0166C50.9 26.0166 53.7545 28.4935 53.7446 31.6709C53.7346 34.8483 50.9 37.3253 47.3178 37.3253Z" fill="#5865F2"/>
              </svg>
              Discord
            </a>
          </div>
        </div>
        <div className="leaderboard-header">
          <h1 className="leaderboard-title">🏆 Leaderboard 🏆</h1>
          <p className="leaderboard-subtitle">Top Winners & Best Messages</p>
        </div>

        <div className="leaderboard-content">
          <div className="leaderboard-section win98-bevel-inset">
            <h2 className="section-title">🏅 Top 10 Daily Contest Winners</h2>
            {topWinners.length === 0 ? (
              <div className="empty-state">
                <p>No winners yet. Be the first to win a daily contest! 🐕</p>
              </div>
            ) : (
              <div className="winners-list">
                {topWinners.map((winner, index) => (
                  <div key={winner.id} className="leaderboard-item">
                    <div className="rank-badge">
                      {getRankEmoji(index + 1)}
                    </div>
                    <div className="item-content">
                      <div className="item-main">
                        <span className="item-name">{he.encode(winner.username)}</span>
                        <span className="item-stats">
                          🏆 {winner.wins} {winner.wins === 1 ? 'Win' : 'Wins'}
                        </span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="leaderboard-section win98-bevel-inset">
            <h2 className="section-title">💬 Daily Winners (Last 7 Days)</h2>
            <p className="section-subtitle">The winning message from each of the last 7 days</p>
            {topMessages.length === 0 ? (
              <div className="empty-state">
                <p>No daily winners in the last 7 days. Start chatting and get upvoted! 🐕</p>
              </div>
            ) : (
              <div className="messages-list">
                {topMessages.map((message, index) => (
                  <div key={message.id} className="message-item">
                    <div className="message-rank">
                      {getRankEmoji(index + 1)}
                    </div>
                    <div className="message-content">
                      <div className="message-header">
                        <span 
                          className="message-author clickable"
                          onClick={() => navigate(`/user/${message.user_id}`)}
                          title="View profile"
                        >
                          {he.encode(message.username)}
                        </span>
                        <span className="message-upvotes">
                          ⬆️ {message.upvote_count} {message.upvote_count === 1 ? 'upvote' : 'upvotes'}
                        </span>
                      </div>
                      <div className="message-text">{he.encode(message.content)}</div>
                      <div className="message-meta">
                        {formatDate(message.created_at)} • Day: {new Date(message.message_date || message.created_at).toLocaleDateString()}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div className="leaderboard-footer">
          <p>💡 Tip: Post great messages and get upvoted to win the daily contest!</p>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard;

